// packages/ui/src/theme/baseTheme.ts
//
// The canonical TourTask MUI theme, shared by every surface that renders the
// shared @tourtask/ui components (the backoffice, and the public site). It is
// the single source of truth for the brand palette + the conventions those
// components are designed against — Inter typography, hidden number-input
// spinners, tooltip sizing, scroll-lock-free menus/popovers, and the component
// rules every TourTask theme shares (`sharedComponents`). Each app layers
// its own additions on top via `createTheme({ ...baseTheme, palette: {
// ...baseTheme.palette, ... } })` — the backoffice adds background/menu colors;
// the public site adds its marketing identity (secondary accent, rounding,
// sentence-case buttons, RSC link behavior).
import type { ThemeOptions } from '@mui/material/styles';
import { BRAND_BLUE, BRAND_BLUE_DARK } from './tokens';
import { sharedComponents } from './sharedComponents';

export const baseTheme: ThemeOptions = {
  palette: {
    primary: { main: BRAND_BLUE, dark: BRAND_BLUE_DARK },
  },
  typography: {
    fontFamily: 'Inter, sans-serif',
  },

  components: {
    ...sharedComponents,
    MuiCssBaseline: {
      styleOverrides: {
        'input[type=number]::-webkit-outer-spin-button, input[type=number]::-webkit-inner-spin-button': {
          display: 'none',
        },
        'input[type=number]': {
          MozAppearance: 'textfield',
        },
      },
    },
    // The step a wizard is on gets a blue ring around its number (a gap in the surface colour, then
    // primary), so where you are reads at a glance — every MUI `Stepper` in the product.
    MuiStepIcon: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-active': {
            borderRadius: '50%',
            boxShadow: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.primary.main}`,
          },
        }),
      },
    },
    MuiMenu: {
      defaultProps: {
        disableScrollLock: true, // Allow scrolling when menus are open
      },
    },
    MuiPopover: {
      defaultProps: {
        disableScrollLock: true, // Allow scrolling when popovers are open
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.875rem',
          lineHeight: 1.5,
          paddingTop: 8,
          paddingBottom: 8,
          paddingLeft: 12,
          paddingRight: 12,
          maxWidth: 360,
        },
      },
    },
  },
};
